import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion, faDownLong, faUpLong } from '@fortawesome/free-solid-svg-icons';
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import QuestionPreviewModal from '../../../../assignments/components/QuestionPreviewModal';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentUserData, selectFerpaState } from '../../../../../features/authentication/authSlice';
import ItemAnalysisHeader from '../components/itemAnalysisHeader';
import TableSkeleton from '../../../../../components/SkeletonLoader/TableSkeleton';
import SkeletonGrid from '../../../../../components/SkeletonLoader/SkeletonGrid';
import { convertToCSV, downloadCSV } from '../../../../assignments/utils';
import { DateFormatter } from '../../../../../utils/DateFormatter';
import { useGetItemAnalysisReportMutation, useGetViewStandardMutation } from '../../../../../features/authentication/reportApiSlice';
import { useItemAnalysisContext } from '../utils/itemAnalysisContext';
import Modals from '../../../../assignments/pages/ManageAssign/components/StudentSessionReport/components/Modals';
import FERPA from '../../../../../components/FERPA/FerpaComponent';
import pasteToExcelGif from '../../../../../assets/images/PasteToExcel.gif';
import Modal from '../../../../../components/Modal';
import PageSize from '../../../../../enums/pageSize';
import usePrintPdfGenerator from '../../../../../utils/PrintPdfGenerator';

const ItemAnalysisReport = () => {
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [openQuestionDetailModel, setOpenQuestionDetailModel] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [reportClasses, setReportClasses] = useState(null);
  const [districtTotals, setDistrictTotals] = useState([]);
  const ferpaEnabled = useSelector(selectFerpaState);
  const [activeTab, setActiveTab] = useState(1);
  const [lastAssignmentIndex, setLastAssignmentIndex] = useState(0);
  const [standardDetails, setStandardDetails] = useState([]);
  const [openStandardModal, setOpenStandardModal] = useState(false);
  const [openCopyToClipboard, setOpenCopyToClipboard] = useState(false);
  const [totalPerQuestion, setTotalPerQuestion] = useState([]);
  const { classes, includeRemoved, masteryScore, primaryAssignmentId, primaryAssignmentName, compare1Id, compare2Id, compare3Id, compare4Id, compare5Id } = useItemAnalysisContext();
  const user = useSelector(selectCurrentUserData);

  const [getItemAnalysisReport] = useGetItemAnalysisReportMutation();
  const [getStandard] = useGetViewStandardMutation();
  const { printPDF, isPdfGenerating } = usePrintPdfGenerator();

  useEffect(() => {
    getItemAnalaysisReportAsync();
  }, []);

  const handleTabSwitch = (tabId) => {
    setActiveTab(tabId);
  };

  const tabs = [
    {
      id: 1,
      name: 'Standards',
      path: 'standards',
    },
    {
      id: 2,
      name: 'Topics',
      path: 'topics',
    },
    {
      id: 3,
      name: 'Tags',
      path: 'tags',
    },
    {
      id: 4,
      name: 'Attributes',
      path: 'attributes',
    }
  ];

  const getItemAnalaysisReportAsync = async () => {
    if (!(primaryAssignmentId > 0) || !(classes.filter(x => x.selected).length > 0))
      return;

    setIsLoading(true);
    var compareAssignmentIds = [];
    if (compare1Id > 0) {
      compareAssignmentIds.push(compare1Id);
    }
    if (compare2Id > 0) {
      compareAssignmentIds.push(compare2Id);
    }
    if (compare3Id > 0) {
      compareAssignmentIds.push(compare3Id);
    }
    if (compare4Id > 0) {
      compareAssignmentIds.push(compare4Id);
    }
    if (compare5Id > 0) {
      compareAssignmentIds.push(compare5Id);
    }

    const params = new URLSearchParams();
    classes.filter(x => x.selected).forEach(item => params.append('classIds', item.id));
    compareAssignmentIds.forEach(item => params.append('compareAssignmentIds', item));
    params.append('primaryAssignmentId', primaryAssignmentId);
    params.append('displayDeleted', includeRemoved);

    const report = await getItemAnalysisReport(params.toString()).unwrap();
    setReport(report.reportData);
    setReportClasses(report.classes);
    setDistrictTotals(report.districtTotal);
    calculateTotalPerQuestion(report.reportData, report.reportData[0]?.assignments.length - 1);
    setLastAssignmentIndex(report.reportData[0]?.assignments.length - 1);
    setIsLoading(false);
  };

  const handleQuestionPreview = (questionId) => {
    setSelectedQuestionId(questionId);
    setOpenQuestionDetailModel(true);
  };

  const handlePrintToPdf = async () => {
    if(isPdfGenerating) return;

    if (Object.keys(report).length === 0)
      return;

    setTimeout(async () => {
      const contentWrapper = document.getElementById('printWrapper');
      const htmlString = contentWrapper.innerHTML;

      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = htmlString;

      const vHeaders = tempContainer.querySelectorAll('th.vertical-header');
      vHeaders.forEach((vHeader) => {
        vHeader.classList.remove('vertical-header');
      });

      const underlineThTags = tempContainer.querySelectorAll('th.text-decoration-underline');
      underlineThTags?.forEach((underlineThTag) => {
        underlineThTag.classList.remove('text-decoration-underline');
      });

      const underlineTdTags = tempContainer.querySelectorAll('td.text-decoration-underline');
      underlineTdTags?.forEach((underlineTdTag) => {
        underlineTdTag.classList.remove('text-decoration-underline');
      });

      const svgElements = tempContainer.querySelectorAll('svg.svg-inline--fa');
      svgElements.forEach((svgElement) => {
        svgElement.classList.add('d-none');
      });

      await printPDF('ItemAnalysisReport', tempContainer.innerHTML, PageSize.A0);
    }, 0);
  };

  const handleExport = () => {
    setIsExporting(true);
    const reportDetails = {
      Assignment: primaryAssignmentName,
      'Exported By': user?.LastName + ' ' + user?.FirstName,
      'Export Date': DateFormatter.toShortDate2(new Date()),
    };

    const studentReport = [];
    var questionRow = generateQuestionKeyRow('question');
    studentReport.push(questionRow);
    var keyRow = generateQuestionKeyRow('key');
    studentReport.push(keyRow);

    report.forEach((student) => {
      let studentRow = generateStudentRow(student);
      studentReport.push(
        studentRow
      );
    });

    let districtTotalRows = generateQuestionTotals();
    districtTotalRows?.forEach((districtTotalRow) => {
      studentReport.push(
        districtTotalRow
      );
    });

    const newData = {
      ReportOptions: reportDetails,
      '': studentReport
    };
    const csvData = convertToCSV(newData);
    downloadCSV(csvData, 'ItemAnalysisReport');
    setIsExporting(false);
  };

  const generateQuestionKeyRow = (rowType) => {
    let row = {
      [report[0].className]: rowType == 'key' ? 'Key' : 'Student Name',
      'Student ID #': '',
      'School': '',
      'ClassTeacher': '',
    };
    report[0].assignments.forEach((assignment) => {
      let assignmentObj = {
        [assignment.assignmentQuestion.description.replace(/,/g, ' ')]: ''
      };
      row = { ...row, ...assignmentObj };
    });
    row = { ...row, 'Raw Score': '', 'Mastery Y/N(65%)': '' };
    report[0].assignments[lastAssignmentIndex].assignmentQuestion.questionProperties.forEach((data, index) => {
      var classNames = `##_${index}_##`;
      const qProperties = activeTab == 1? data.standards: activeTab == 2 ? data.topics : activeTab == 3 ? data.tags : data.attributes;
      
      qProperties?.forEach((standard, index) => {
        classNames += (activeTab == 1?standard.standardNumber:standard.name).replace(/,/g, ' ');
        classNames += (index + 1 == qProperties?.length ? '' : '; ');
      });
      let keyObj = {
        [classNames]: rowType == 'key' ? data.key : `Q${index + 1}`
      };
      row = { ...row, ...keyObj };
    });
    return row;
  };

  const generateStudentRow = (student) => {
    let studentRow = {
      [report.className]: student.lName + ' ' + student.fName,
      'Student ID #': student.studentId,
      'School': student.schoolName,
      'ClassTeacher': student.teacherName,
    };
    student.assignments.forEach((assignment) => {
      let assignmentObj = {
        [assignment.assignmentQuestion.description]: Math.round((assignment.points / assignment.assignmentQuestion.maxScore) * 100)
      };
      studentRow = { ...studentRow, ...assignmentObj };
    });
    studentRow = {
      ...studentRow,
      'Raw Score': student.assignments[lastAssignmentIndex].points,
      'Mastery Y/N(65%)': ((student.assignments[lastAssignmentIndex].points / student.assignments[lastAssignmentIndex].assignmentQuestion.maxScore) * 100) >= masteryScore ? 'Yes' : 'No'
    };

    student.assignments[lastAssignmentIndex].assignmentQuestion.questionProperties.forEach((data, index) => {
      var classNames = `##_${index}_##`;
      const qProperties = activeTab == 1? data.standards: activeTab == 2 ? data.topics : activeTab == 3 ? data.tags : data.attributes;
      qProperties?.forEach((standard) => {
        classNames += (activeTab == 1?standard.standardNumber:standard.name) + '/';
      });
      let studentObj = {
        [classNames]: student.assignments[lastAssignmentIndex].questionAnswerPoints[index]
      };
      studentRow = { ...studentRow, ...studentObj };
    });

    return studentRow;
  };

  const generateQuestionTotals = () => {
    let studentInfo = {
      [report[0].className]: '',
      'Student ID #': '',
      'School': '',
      'ClassTeacher': '',
    };

    var assignmentColumns = {};
    report[0].assignments.forEach((assignment) => {
      let assignmentObj = {
        [assignment.assignmentQuestion.description]: ''
      };
      assignmentColumns = { ...assignmentColumns, ...assignmentObj };
    });

    var qTotal = {};
    report[0].assignments[lastAssignmentIndex].assignmentQuestion.questionProperties.forEach((item, index) => {
      var classNames = `##_${index}_##`;
      const qProperties = activeTab == 1? item.standards: activeTab == 2 ? item.topics : activeTab == 3 ? item.tags : item.attributes;
      qProperties?.forEach((standard) => {
        classNames += (activeTab == 1?standard.standardNumber:standard.name) + '/';
      });
      let questionObj = {
        [classNames]: totalPerQuestion[index] > 0 ? totalPerQuestion[index] : ''
      };
      qTotal = { ...qTotal, ...questionObj };
    });

    var qPercentage = {};
    report[0].assignments[lastAssignmentIndex].assignmentQuestion.questionProperties.forEach((item, index) => {
      var classNames = `##_${index}_##`;
      const qProperties = activeTab == 1? item.standards: activeTab == 2 ? item.topics : activeTab == 3 ? item.tags : item.attributes;
      qProperties?.forEach((standard) => {
        classNames += (activeTab == 1?standard.standardNumber:standard.name) + '/';
      });
      let questionObj = {
        [classNames]: Math.round((totalPerQuestion[index] / report.length) * 100)
      };
      qPercentage = { ...qPercentage, ...questionObj };
    });

    var rows = [];
    rows.push({
      ...studentInfo,
      ...assignmentColumns,
      'Raw Score': '',
      'Mastery Y/N(65%)': 'Total Correct Per Question',
      ...qTotal
    });
    rows.push({
      ...studentInfo,
      ...assignmentColumns,
      'Raw Score': '',
      'Mastery Y/N(65%)': 'Percent Correct Per Question',
      ...qPercentage
    });

    districtTotals?.schoolTotals?.map((schoolTotal) => {
      var schoolTotalValues = {};
      report[0].assignments[lastAssignmentIndex].assignmentQuestion.questionProperties.forEach((item, qIndex) => {
        var classNames = `##_${qIndex}_##`;
        item.standards?.forEach((standard) => {
          classNames += standard.standardNumber + '/';
        });
        let questionObj = {
          [classNames]: schoolTotal.questionTotals[qIndex].correctPercentage
        };
        schoolTotalValues = { ...schoolTotalValues, ...questionObj };
      });

      rows.push({
        ...studentInfo,
        ...assignmentColumns,
        'Raw Score': '',
        'Mastery Y/N(65%)': 'Percent Correct in '+schoolTotal.name,
        ...schoolTotalValues
      });
    });

    return rows;
  };

  const handleStandardPreview = async (standard) => {
    const option = {
      stateCourseId: parseInt(standard.stateCourseId),
      standardNumber: String(standard.standardNumber),
      stateId: standard.stateId
    };
    try {
      const data = await getStandard(option).unwrap();
      setStandardDetails(data);
      setOpenStandardModal(true);
    } catch (error) {
      console.log(error);
    }
  };

  const truncateName = (name) => {
    const maxLength = 22;
    return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;
  };

  const handleCopyToClipboard = () => {
    const table = document.getElementById('reportTable');
    if (!table) {
      return;
    }

    const container = document.createElement('div');

    const clonedTable = table.cloneNode(true);

    const applyStyles = (element) => {
      const computedStyles = window.getComputedStyle(element);
      for (const property of computedStyles) {
        const value = computedStyles.getPropertyValue(property);
        if (value) {
          element.style[property] = value;
        }
      }
    };

    const applyStylesRecursively = (node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        applyStyles(node);
        if (node.tagName === 'TD' || node.tagName === 'TH') {
          node.style.width = '10px';
          node.style.textAlign = 'center';
          node.style.fontSize = '13.5px';
          node.style.fontFamily = 'Arial';
          node.style.fontWeight = 'bold';
          node.style.border = '1px solid black';
          node.style.backgroundColor = '#F3F4F8';
        }

        if (
          node.classList.contains('text-end')
        ) {
          node.style.textAlign = 'right';
        }
        if (
          node.classList.contains('bg-md-gray')
        ) {
          node.style.backgroundColor = '#EBEEF6';
        }
        if (
          node.classList.contains('bg-cyan')
        ) {
          node.style.backgroundColor = '#E3F6FA';
        }
        if (
          node.classList.contains('bg-lightYellow')
        ) {
          node.style.backgroundColor = '#FFFACB';
        }
        if (
          node.classList.contains('bg-aboveMastery')
        ) {
          node.style.backgroundColor = '#F3F4F8';
        }
        if (
          node.classList.contains('bg-lightPink')
        ) {
          node.style.backgroundColor = '#FFE5F3';
        }

        Array.from(node.children).forEach(applyStylesRecursively);
      }
    };

    applyStylesRecursively(clonedTable);

    container.appendChild(clonedTable);

    container.querySelector('table').setAttribute(
      'style',
      'border-collapse: collapse; width: 100%;'
    );

    const tableHtml = container.innerHTML;

    navigator.clipboard
      .write([
        new ClipboardItem({
          'text/html': new Blob([tableHtml], { type: 'text/html' }),
        }),
      ])
      .then(() => {
        setOpenCopyToClipboard(true);
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
      });
  };

  const calculateTotalPerQuestion = (reportData, lastAssignmentIndex) => {
    const totaPoint = [];
    reportData.map(student => {
      student.assignments[lastAssignmentIndex]?.questionAnswerPoints.map((point, index) => {
        const pointValue = parseInt(point, 10);
        if (Number.isInteger(pointValue)) {
          totaPoint[index] = (totaPoint[index] || 0) + pointValue;
        } else {
          totaPoint[index] = totaPoint[index] || 0;
        }
      });
    });
    setTotalPerQuestion(totaPoint);
  };

  const maxTotalPerQuestion = Math.max(...totalPerQuestion);
  const minTotalPerQuestion = Math.min(...totalPerQuestion);

  return (
    <>
      <div className="white-card">
        <ItemAnalysisHeader
          isPageLoading={isLoading}
          printHandler={handlePrintToPdf}
          exportHandler={handleExport}
          isExporting={isExporting}
          isPdfGenerating={isPdfGenerating}
          handleCopyToClipboard={handleCopyToClipboard}
        />
        {(report?.length > 0 && !isLoading) ? (
          <>
            <div className="primary-tab justify-content-between mt-4 d-flex">
              <div className="btn-group">
                {tabs.map((item) => (
                  <button
                    key={item.id}
                    className={`btn ${activeTab == item.id ? 'active' : ''}`}
                    onClick={() => handleTabSwitch(item.id)}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
              <div className='align-items-center d-flex gap-4'>
                <FERPA />
              </div>
            </div>
            <div id='printWrapper'>
              <Row className='p-3 scoreGrid m-1 no-select'>
                <Col className='p-2 scoreBlock fw-semibold text-center'>Legend</Col>
                <Col className='p-2 scoreBlock text-center lowScore mr-2'>Low Score <FontAwesomeIcon icon={faDownLong} color='#7D7D7D' /> </Col>
                <Col className='p-2 scoreBlock text-center bg-lightPink mr-2'>Score {'<'} {masteryScore}</Col>
                <Col className='p-2 scoreBlock text-center mr-2 bg-lightYellow'>{masteryScore} {'≤'} score {'≤'} {masteryScore + 9}</Col>
                <Col className='p-2 scoreBlock text-center mr-2 bg-aboveMastery'>{masteryScore + 9} {'<'} score</Col>
                <Col className='p-2 scoreBlock text-center mr-2 highScore'>High Score <FontAwesomeIcon icon={faUpLong} color='#7D7D7D' /></Col>
                <Col className='p-2 col-3 text-center bg-aboveMastery scoreBlock'>* Correct on Retry 1st answer shown</Col>
              </Row>
              <div className='table-responsive transparent-scroll'>
                <table className='styled-table' id='reportTable'>
                  <thead>
                    <tr>
                      <th className='text-center min-w-150'>
                        {
                          reportClasses?.slice(0, 3).map((className, index) => {
                            return (<div key={`classNames_${index}`}>{className}</div>);
                          })
                        }
                        {reportClasses?.length - 3 > 0 &&
                          <OverlayTrigger
                            key=""
                            placement="top"
                            overlay={
                              <Tooltip className="custom-tooltip">
                                {
                                  reportClasses.slice(3).map((className, index) => {
                                    return (<div key={`classNames-tt_${index}`}>{className}</div>);
                                  })
                                }
                              </Tooltip>
                            }>
                            <div className='btn-link'>
                              <u>{'&'}&nbsp;{reportClasses.length - 3} More Classes...</u>
                            </div>
                          </OverlayTrigger>
                        }
                      </th>
                      <th className='text-center min-w-150'>Student ID #</th>
                      <th className='text-center min-w-150'>School</th>
                      <th className='text-center min-w-150'>Class Teacher</th>
                      {report[0]?.assignments.map((assignment) => {
                        return (
                          <>
                            <th className='text-center bg-md-gray'>
                              <span>{assignment.assignmentQuestion.description}</span></th>
                          </>
                        );
                      })}
                      <th className='text-center  bg-md-gray'>Raw Score</th>
                      <th className='text-center bg-md-gray'>Mastery Y/N ({masteryScore}%)</th>
                      {report[0]?.assignments[lastAssignmentIndex]?.assignmentQuestion.questionProperties.map((item, index) => {
                        return (
                          <th className='text-center fw-semibold bg-cyan vertical-header text-decoration-underline cursor-pointer mh-25'
                            key={`pp_th_${index}`}>
                            {activeTab == 1 && <span className=''>{item.standards?.map((standard, index) => {
                              return (
                                <OverlayTrigger
                                  key={`std_o_${index}`}
                                  placement="top"
                                  overlay={<Tooltip className="custom-tooltip">{standard.standardNumber}</Tooltip>}>
                                  <span key={`std_${index}`} onClick={() => handleStandardPreview(standard)} data-full-value={standard.standardNumber} >{truncateName(standard.standardNumber)}{index < item.standards?.length - 1 ? ', ' : ''}</span>
                                </OverlayTrigger>
                              );
                            })}</span>
                            }
                            {activeTab != 1 && <span className=''>{(activeTab == 2 ? item.topics : (activeTab == 3 ? item.tags : item.attributes))?.map((topic, index) => {
                              return (
                                <OverlayTrigger
                                  key={`topicO_${index}`}
                                  placement="top"
                                  overlay={<Tooltip className="custom-tooltip">{topic.name}</Tooltip>}>
                                  <span key={`topic_${index}`} data-full-value={topic.name}>{truncateName(topic.name)}{index < (activeTab == 2 ? item.topics : (activeTab == 3 ? item.tags : item.attributes)).length - 1 ? ', ' : ''}</span>
                                </OverlayTrigger>
                              );
                            })}</span>
                            }
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className='text-center'>
                        Student Name&nbsp;
                        <OverlayTrigger
                          key="label-info"
                          placement="top"
                          overlay={
                            <Tooltip id="parent-tooltip" className="image-info-tooltip">
                              <div className='d-flex align-items-center gap-2'><span className='fs-4'>*</span><span>Indicates removed students from the classes</span></div>
                            </Tooltip>
                          }>
                          <FontAwesomeIcon icon={faCircleQuestion} className="circle-question mx-1" />
                        </OverlayTrigger>
                      </td>
                      <td colSpan={6 + lastAssignmentIndex}></td>
                      {report[0]?.assignments[lastAssignmentIndex]?.assignmentQuestion.questions.questionIds?.map((questionId, index) => {
                        return (
                          <td className='text-center fw-semibold text-decoration-underline' key={`Q_${index}`}>
                            <span className='cursor-pointer' onClick={() => handleQuestionPreview(questionId)}>Q{index + 1}</span></td>
                        );
                      })}
                    </tr>
                    <tr>
                      <td className='text-center'>Key</td>
                      <td colSpan={6 + lastAssignmentIndex}></td>
                      {report[0]?.assignments[lastAssignmentIndex]?.assignmentQuestion.questionProperties?.map((item, index) => {
                        return (
                          <td className='text-center' key={`key_${index}`} dangerouslySetInnerHTML={{ __html: item.key }}></td>
                        );
                      })}
                    </tr>
                    {report.map((student, index) => {
                      return (
                        <tr key={`StudTr_${index}`}>
                          <td className='text-center bg-cyan'>{ferpaEnabled ? <span><span>{student.lName}&nbsp;{student.fName}</span> <span className='text-danger fs-5'>{student.deleted ? '*' : ''}</span></span> : <span>name hidden</span>}</td>
                          <td className='text-center'>{ferpaEnabled ? student.identification : 'ID hidden'}</td>
                          <td className='text-center'>{student.schoolName}</td>
                          <td className='text-center'>{student.teacherName}</td>
                          {student.assignments.map((assignment, index) => {
                            var score = Math.round((assignment.points / assignment.assignmentQuestion.maxScore) * 100);
                            return (
                              <td className={`${(index == lastAssignmentIndex) ? (score < masteryScore ? 'bg-lightPink' : score > masteryScore + 9 ? 'bg-aboveMastery' : 'bg-lightYellow') : ''} text-center ${(assignment.hasHighest ? assignment.isHighest ? 'highScore ' : 'lowScore' : '')}`} key={`level_${student.identification}_${index}`}>
                                <span className='mr-1'>
                                  {score}%
                                </span>
                                {assignment.hasHighest && <span>
                                  {assignment.isHighest ? <FontAwesomeIcon icon={faUpLong} color='#7D7D7D' /> : <FontAwesomeIcon icon={faDownLong} color='#7D7D7D' />}
                                </span>
                                }
                              </td>
                            );
                          })}
                          <td className='text-center bg-md-gray'>{student.assignments[lastAssignmentIndex].points}</td>
                          <td className='text-center bg-md-gray'>{((student.assignments[lastAssignmentIndex].points / student.assignments[lastAssignmentIndex].assignmentQuestion.maxScore) * 100) >= masteryScore ? 'Yes' : 'No'}</td>
                          {student.assignments[lastAssignmentIndex]?.questionAnswerPoints.map((questionScore, questionIndex) => {
                            return (
                              <td className='text-center' key={`Qs_${student.identification}${questionIndex}`}>{questionScore}</td>
                            );
                          })}
                        </tr>
                      );
                    })}
                    <tr>
                      <td colSpan={7 + lastAssignmentIndex} className='text-end'>Total Correct Per Question</td>
                      {report[0]?.assignments[lastAssignmentIndex]?.assignmentQuestion.questionProperties?.map((item, index) => {
                        return (
                          <td className='text-center' key={`tCorrectQ_${index}`}>{totalPerQuestion[index] > 0 ? totalPerQuestion[index] : ''}</td>
                        );
                      })}
                    </tr>
                    <tr>
                      <td className='text-end' colSpan={7 + lastAssignmentIndex}>Percent Correct Per Question</td>
                      {report[0]?.assignments[lastAssignmentIndex]?.assignmentQuestion.questionProperties?.map((item, index) => {
                        return (
                          <td className={`text-center min-w-50 ${minTotalPerQuestion != maxTotalPerQuestion && maxTotalPerQuestion > 0 ? totalPerQuestion[index] == maxTotalPerQuestion ? 'highScore ' : 'lowScore' : ''}`} key={`perQ_${index}`}>
                            {minTotalPerQuestion != maxTotalPerQuestion && maxTotalPerQuestion > 0 && <span>
                              {totalPerQuestion[index] == maxTotalPerQuestion ? <FontAwesomeIcon icon={faUpLong} color='#7D7D7D' /> : <FontAwesomeIcon icon={faDownLong} color='#7D7D7D' />}
                            </span>
                            }
                            {Math.round((totalPerQuestion[index] / report.length) * 100)}%

                          </td>
                        );
                      })}
                    </tr>

                    {districtTotals?.schoolTotals?.map((schoolTotal, index) => {
                      return (<tr key={`sTotal_tr_${index}`}>
                        <td className='text-end' colSpan={7 + lastAssignmentIndex} key={`sTotal_td_${index}`}>{'Percent Correct in '}{schoolTotal.name}</td>
                        {schoolTotal.questionTotals.map((total, index2) => {
                          return (
                            <td className='text-center' key={index + '_' + index2}>{total.correctPercentage}%</td>
                          );
                        })}
                      </tr>);
                    })
                    }
                  </tbody>
                </table>
              </div>
            </div>
          </>) : (
          <>
            <div className='mt-4 w-100'>
              <SkeletonGrid
                rowCount={1}
                colCount={1} />
            </div>
            <div className='mt-4 w-100'>
              <SkeletonGrid
                rowCount={1}
                colCount={1}
                className='p-3' />
            </div>
            <div className='mt-4 w-100'>
              <TableSkeleton
                colCount={7}
                rowCount={5}
              />
            </div>
          </>
        )}
      </div>
      {openQuestionDetailModel &&
        <QuestionPreviewModal
          isOpen={openQuestionDetailModel}
          onClose={() => setOpenQuestionDetailModel(false)}
          questionId={selectedQuestionId}
          assignmentId={parseInt(primaryAssignmentId)}
        />
      }

      <Modals
        isOpen={openStandardModal}
        onClose={() => setOpenStandardModal(false)}
        details={standardDetails}
      />

      <Modal
        isOpen={openCopyToClipboard}
        onClose={() => setOpenCopyToClipboard(false)}
        contentWidth={'75%'}
        titleName={'Table copied to clipboard!'}
      >
        <Row className='px-4'>
          <Col className='col-4'>
            <Row className='mb-2'><Col className='fw-500 mb-1'>Now, follow these steps:</Col></Row>
            <Row className='fw-500'>
              <ol>
                <li>Open a blank document in your spreadsheet application</li>
                <li>Select the first cell (A1)</li>
                <li>Paste in your data.</li>
              </ol>
            </Row>
            <Row><Col className='d-flex justify-content-end'>
              <button
                className='btn btn-castleRed w-50'
                onClick={() => setOpenCopyToClipboard(false)}
              >
                Ok&nbsp;
              </button></Col></Row>
          </Col>
          <Col className='col-8'><img className='w-100' src={pasteToExcelGif} /></Col>
        </Row>
      </Modal>
    </>
  );
};

export default ItemAnalysisReport;